import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CreateReminderRequest } from "@shared/reminders";

export function useReminders(opts: { status?: "pending" | "all"; assignedBy?: "me" } = {}) {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.assignedBy) params.set("assignedBy", opts.assignedBy);
  return useQuery({
    queryKey: ["reminders", opts],
    queryFn: () => fetch(`/api/reminders?${params}`, { credentials: "include" }).then(r => r.json()),
    staleTime: 30_000,
  });
}

export function useCreateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReminderRequest) =>
      fetch("/api/reminders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      }).then(async r => { if (!r.ok) throw new Error((await r.json()).error || "Failed"); return r.json(); }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reminders"] });
      qc.invalidateQueries({ queryKey: ["entityReminders"] });
      qc.invalidateQueries({ queryKey: ["record-summary"] });
    },
  });
}

export function useReminderAction() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["reminders"] });
    qc.invalidateQueries({ queryKey: ["entityReminders"] });
    qc.invalidateQueries({ queryKey: ["record-summary"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };
  return {
    snooze: (id: string, duration: "1h" | "1d" | string) =>
      fetch(`/api/reminders/${id}/snooze`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ duration }) }).then(invalidate),
    complete: (id: string) =>
      fetch(`/api/reminders/${id}/complete`, { method: "POST", credentials: "include" }).then(invalidate),
    cancel: (id: string, scope: "this" | "this-and-future" = "this") =>
      fetch(`/api/reminders/${id}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ scope }) }).then(invalidate),
    patch: (id: string, body: any) =>
      fetch(`/api/reminders/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) }).then(invalidate),
  };
}
